import { giphyLightGrey, giphyWhite } from '@giphy/js-brand'
import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { fetchDashboardTrendingSearches } from 'shared/api/index'
import { SearchIcon, TrendingIcon } from 'shared/components/homepage-icons'
import Observer from 'shared/components/observer'
import { highestZIndex } from 'shared/css/z-indexes'
import useAsyncEffect from 'shared/hooks/use-async-effect'
import { googleAnalyticsEvent } from 'shared/util/analytics'
import { mobile } from 'shared/util/media-query'
import styled from 'styled-components'
import { GradientButton } from '../../site/creation-tools/components/buttons'
import CreationToolGradient from '../../site/creation-tools/types/gradient'
import AnalyticsInfoTooltip from '../../site/user-dashboard-analytics/analytics-info-tooltip'

const TrendingSearchesContainer = styled.div`
    margin-top: 20px;
    margin-bottom: 25px;
    display: flex;
    justify-content: center;
    align-items: center;
    background: linear-gradient(-180deg, rgb(33, 33, 33) 0%, rgb(24, 24, 24) 98%);
    color: ${giphyLightGrey};
    border-radius: 4px;
    flex-flow: row wrap;
    padding-top: 40px;
    padding-bottom: 40px;
`

const Title = styled.div`
    font-weight: bold;
    font-size: 26px;
    font-family: NexaBlack;
    letter-spacing: 0px;
    color: ${giphyWhite};
`

const Info = styled.div`
    font-size: 16px;
    font-weight: bold;
    letter-spacing: 0px;
    line-height: 22px;
    margin-top: 18px;
    & a {
        color: ${giphyLightGrey};
        text-decoration: underline;
    }
`

const EditorialCalendarButton = styled(GradientButton)`
    border-radius: 2px;
    height: 36px;
    width: 220px;
    margin-top: 10px;
    ${mobile.css`
        margin-left: auto;
        margin-right: auto;
    `}
    z-index: ${highestZIndex};
`

const TagsObserver = styled(Observer)`
    width: 100%;
`

const TagsContainer = styled.div`
    margin-top: 25px;
    margin-bottom: 25px;
    width: 100%;
    height: 150px;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
`

const Tag = styled.div`
    color: ${giphyWhite};
    max-width: 211px;
    height: 42px;
    background: linear-gradient(-180deg, rgb(40, 40, 40) 0%, rgb(33, 33, 32) 100%);
    border-radius: 25px;
    font-size: 17px;
    font-family: InterFace;
    font-weight: bold;
    letter-spacing: 0px;
    line-height: 42px;
    text-align: center;
    & svg {
        height: 15px;
        width: 20px;
    }
    cursor: pointer;
    user-select: none;
    display: block;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    padding-left: 10px;
    padding-right: 18px;
    margin-top: 10px;
    justify-self: center;
`

const ToggleContainer = styled.div`
    width: 700px;
    height: 42px;
    margin-top: 25px;
    background: rgb(18, 18, 18);
    border-radius: 21px;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(233px, 1fr));
`

const TooltipIcon = styled(AnalyticsInfoTooltip)`
    display: inline-block;
    & strong {
        font-weight: bold;
    }
`

type ToggleProps = {
    toggle: boolean
}

const TogglePill = styled.div<ToggleProps>`
    width: auto;
    height: 42px;
    border-radius: 22.5px;
    cursor: pointer;
    text-align: center;
    ${(props) => {
        if (props.toggle) {
            return `
                background: rgba(0, 0, 0, 0.3)
                linear-gradient(45deg, rgb(0, 204, 255) 0%, rgb(0, 255, 153) 100%);
                .ss-info {
                    color: ${giphyWhite} !important;
                }
            `
        }
    }}
`

const ToggleTitle = styled.div`
    margin: 0;
    color: ${giphyWhite};
    font-family: interface;
    font-weight: 600;
    font-size: 16px;
    line-height: 42px;
    text-transform: none;
    display: inline;
`

type TrendingSearch = {
    url: string
    query: string
}

type TrendingSearches = {
    data: {
        trendingSearches: TrendingSearch[]
        tailoredForYou: TrendingSearch[]
        contentGaps: TrendingSearch[]
    }
}

const ToggleTitles = {
    'tailored-for-you': 'Tailored for You',
    'trending-searches': 'Trending Searches',
    'content-gaps': 'Content Gaps',
}

const ToggleGAEvent = (label) => {
    googleAnalyticsEvent({
        category: 'dashboard',
        action: 'Search Insights - Toggle',
        label: label,
    })
}

const DisplayTags = ({ trendingData, toggle }) => {
    let data: TrendingSearch[] = []
    let actionTitle: string = ToggleTitles[toggle]
    if (toggle === 'trending-searches') {
        data = trendingData.data.trendingSearches
    } else if (toggle === 'tailored-for-you') {
        data = trendingData.data.tailoredForYou
    } else if (toggle === 'content-gaps') {
        data = trendingData.data.contentGaps
    }

    return (
        <TagsContainer>
            {data.map((item) => {
                return (
                    <Tag
                        key={item.url}
                        {...{
                            as: 'a',
                            href: item.url,
                            target: '_blank',
                            rel: 'noopener noreferrer',
                            onClick: () =>
                                googleAnalyticsEvent({
                                    category: 'dashboard',
                                    action: `${actionTitle} - Term Click`,
                                    label: item.query,
                                }),
                        }}
                    >
                        {toggle === 'trending-searches' ? <TrendingIcon /> : <SearchIcon />} {item.query}
                    </Tag>
                )
            })}
        </TagsContainer>
    )
}

const DashboardTrendingSearches = ({ user, isDashboardLoaded }) => {
    const contentRef = useRef<HTMLDivElement>(null)
    const sourceBanner = useMemo(() => {
        const urlParams = new URLSearchParams(location.search)
        return urlParams.get('source')
    }, [location.search])

    const [toggle, setToggle] = useState<string>('trending-searches')
    const [trendingData, setTrendingData] = useState<TrendingSearches>()
    const [hasTailoredForYou, setHasTailoredForYou] = useState<boolean>(false)

    useAsyncEffect(async () => {
        const response = await fetchDashboardTrendingSearches(user.username)
        setTrendingData(response)
        if (response.data.tailoredForYou.length > 0) {
            setToggle('tailored-for-you')
            setHasTailoredForYou(true)
        }
    }, [])

    useEffect(() => {
        if (!isDashboardLoaded || !sourceBanner || !trendingData) {
            return
        }

        contentRef.current?.scrollIntoView()
    }, [isDashboardLoaded, sourceBanner, trendingData])

    const uploadingLink = (
        <a
            href="/upload"
            target="_blank"
            onClick={() =>
                googleAnalyticsEvent({
                    category: 'dashboard',
                    action: 'Trending Searches - Uploading Link',
                    label: null,
                })
            }
        >
            Uploading
        </a>
    )

    const taggingLink = (
        <a
            href="https://support.giphy.com/hc/en-us/articles/360026405951-How-to-Tag-Your-Content"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() =>
                googleAnalyticsEvent({
                    category: 'dashboard',
                    action: 'Trending Searches - Tagging Link',
                    label: null,
                })
            }
        >
            Tagging
        </a>
    )

    const trendingContainerVisible = (d: TrendingSearches) => {
        if (hasTailoredForYou) {
            googleAnalyticsEvent({
                category: 'dashboard',
                action: 'Tailored for You - All Tag Names',
                label: d.data.tailoredForYou.map(({ query }) => query ?? '').join(', '),
            })
        }
        googleAnalyticsEvent({
            category: 'dashboard',
            action: 'Trending Searches - All Tag Names',
            label: d.data.trendingSearches.map(({ query }) => query ?? '').join(', '),
        })
        googleAnalyticsEvent({
            category: 'dashboard',
            action: 'Content Gaps - All Tag Names',
            label: d.data.contentGaps.map(({ query }) => query ?? '').join(', '),
        })
    }

    const handleToggle = (t: string) => {
        setToggle(t)
        ToggleGAEvent(ToggleTitles[t])
    }

    let tailoredTooltipText = (
        <Fragment>
            <strong>Tailored for You</strong> terms are high performing tags on your existing content. Pro tip: make
            this high performing content available in all formats - GIFs, Stickers, and Clips.
        </Fragment>
    )
    let trendingTooltipText = (
        <Fragment>
            <strong>Trending Searches</strong> are the most popular search terms on GIPHY refreshed daily.
        </Fragment>
    )
    let gapsTooltipText = (
        <Fragment>
            <strong>Content Gaps</strong> are highly searched terms on GIPHY in need of more results. Be one of the
            first to fill these gaps with relevant content from your channel.
        </Fragment>
    )

    const title = 'GIPHY Search Insights 🔥'

    if (trendingData) {
        return (
            <div ref={contentRef}>
                <TrendingSearchesContainer>
                    <Title>{title}</Title>
                    <Info>
                        Maximize your impact by {uploadingLink} and {taggingLink} content for these search terms!
                    </Info>
                    <ToggleContainer>
                        {hasTailoredForYou && (
                            <TogglePill
                                toggle={toggle === 'tailored-for-you'}
                                onClick={() => handleToggle('tailored-for-you')}
                            >
                                <ToggleTitle>Tailored for You</ToggleTitle>
                                <TooltipIcon tooltip={tailoredTooltipText} />
                            </TogglePill>
                        )}
                        <TogglePill
                            toggle={toggle === 'trending-searches'}
                            onClick={() => handleToggle('trending-searches')}
                        >
                            <ToggleTitle>Trending Searches</ToggleTitle>
                            <TooltipIcon tooltip={trendingTooltipText} />
                        </TogglePill>
                        <TogglePill toggle={toggle === 'content-gaps'} onClick={() => handleToggle('content-gaps')}>
                            <ToggleTitle>Content Gaps</ToggleTitle>
                            <TooltipIcon tooltip={gapsTooltipText} />
                        </TogglePill>
                    </ToggleContainer>
                    <TagsObserver onVisible={() => trendingContainerVisible(trendingData)}>
                        <DisplayTags trendingData={trendingData} toggle={toggle} />
                    </TagsObserver>
                    <Info>Want more inspiration? Plan ahead for high traffic moments throughout the year.</Info>
                    <EditorialCalendarButton
                        {...{
                            as: 'a',
                            href: 'https://support.giphy.com/hc/en-us/articles/360059071691-2022-Content-Calendar',
                            colors: CreationToolGradient.colors(CreationToolGradient.IndigoPurple),
                            target: '_blank',
                            rel: 'noopener noreferrer',
                            onClick: () =>
                                googleAnalyticsEvent({
                                    category: 'dashboard',
                                    action: 'Search Insights - Content Calendar Button',
                                    label: null,
                                }),
                        }}
                    >
                        GIPHY Content Calendar
                    </EditorialCalendarButton>
                </TrendingSearchesContainer>
            </div>
        )
    }
    return <div></div>
}

export default DashboardTrendingSearches
